import { Box, Button, ButtonGroup } from '@mui/material';
import { getEventCounts } from 'api/analyticsReport/getEventCounts';
import { getPageViews } from 'api/analyticsReport/getPageViews';
import { getUserSources } from 'api/analyticsReport/getUserSources';
import { getVisitStats } from 'api/analyticsReport/getVisitStats';
import VisitStatsChart from 'containers/pages/analyticsReport/components/VisitStatsChart';
import EventCountChart from 'containers/pages/analyticsReport/components/EventCountChart';
import PageViewsChart from 'containers/pages/analyticsReport/components/PageViewChart';
import UserSourceChart from 'containers/pages/analyticsReport/components/UserSourceChart';
import AnalyticsDateRangeSelector from 'containers/pages/analyticsReport/components/RangeSelectors';
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import { useNotificationProvider } from 'hooks';
import { getBasicStats } from 'api/analyticsReport/getBasicStats';
import BasicStatsData from 'containers/pages/analyticsReport/components/BasicStatsChart';


type ReportType =
	| 'Basic Stats' 
	| 'Visit Stats'
	| 'User Sources'
	| 'Page Views'
	| 'Event Counts';

const AnalyticsReport: React.FC = () => {
	const [reportType, setReportType] =
		React.useState<ReportType>('Basic Stats');
	const [startDate, setStartDate] = React.useState<Dayjs | null>(null);
	const [endDate, setEndDate] = React.useState<Dayjs | null>(null);
	const [basicStats, setBasicStats] = React.useState<BasicStatsData | null>(
		null,
	);
	const [visitStats, setVisitStats] =
		React.useState<AnalyticsApiResponse | null>(null);
	const [userSources, setUserSources] =
		React.useState<AnalyticsApiResponse | null>(null);
	const [pageViews, setPageViews] =
		React.useState<AnalyticsApiResponse | null>(null);
	const [eventCounts, setEventCounts] =
		React.useState<AnalyticsApiResponse | null>(null);
	const { notifyError } = useNotificationProvider();

	React.useEffect(() => {
		// Set the start and end date when the component mounts
		const today = dayjs(); // Current date
		const sevenDaysAgo = dayjs().subtract(7, 'day'); // Date 7 days ago

		setStartDate(sevenDaysAgo);
		setEndDate(today);
	}, []); // Empty dependency array ensures this runs only on component mount

	// fetch visit stats on start date and end date change
	React.useEffect(() => {
		if (startDate && endDate) {
			if (reportType === 'Basic Stats') {
				getBasicStats(startDate.toDate(), endDate.toDate())
					.then((response) => {
						setBasicStats(response);
					})
					.catch((error) => {
						notifyError('Error fetching basic stats');
						setBasicStats(null);
						console.error('Error fetching basic stats', error);
					});
			}

			if (reportType === 'Visit Stats') {
				getVisitStats(startDate.toDate(), endDate.toDate())
					.then((response) => {
						setVisitStats(response);
					})
					.catch((error) => {
						notifyError('Error fetching visit stats');
						setVisitStats(null);
						console.error('Error fetching visit stats', error);
					});
			}
			if (reportType === 'User Sources') {
				getUserSources(startDate.toDate(), endDate.toDate())
					.then((response) => {
						setUserSources(response);
					})
					.catch((error) => {
						notifyError('Error fetching user sources');
						setUserSources(null);
						console.error('Error fetching user sources', error);
					});
			}
			if (reportType === 'Page Views') {
				getPageViews(startDate.toDate(), endDate.toDate())
					.then((response) => {
						setPageViews(response);
					})
					.catch((error) => {
						notifyError('Error fetching page views');
						setPageViews(null);
						console.error('Error fetching page views', error);
					});
			}

			if (reportType === 'Event Counts') {
				getEventCounts(startDate.toDate(), endDate.toDate())
					.then((response) => {
						setEventCounts(response);
					})
					.catch((error) => {
						notifyError('Error fetching event counts');
						setEventCounts(null);
						console.error('Error fetching event counts', error);
					});
			}
		}
	}, [startDate, endDate, reportType]);
	// this is the component that will be rendered

	return (
		<Box display={'flex'} flexDirection={'column'} flexGrow={1}>
			<AnalyticsDateRangeSelector
				startDate={startDate}
				endDate={endDate}
				setStartDate={setStartDate}
				setEndDate={setEndDate}
			/>
			<Box
				display={'flex'}
				overflow={'auto'}
				padding={2}
				justifyContent={{ xs: 'start', md: 'center' }}
			>
				<ButtonGroup variant="outlined" aria-label="Basic button group">
					<Button
						onClick={() => setReportType('Basic Stats')}
						variant={
							reportType === 'Basic Stats'
								? 'contained'
								: 'outlined'
						}
					>
						Basic Stats
					</Button>
					<Button
						onClick={() => setReportType('Visit Stats')}
						variant={
							reportType === 'Visit Stats'
								? 'contained'
								: 'outlined'
						}
					>
						Visit Stats
					</Button>
					<Button
						onClick={() => setReportType('User Sources')}
						variant={
							reportType === 'User Sources'
								? 'contained'
								: 'outlined'
						}
					>
						User Sources
					</Button>
					<Button
						onClick={() => setReportType('Page Views')}
						variant={
							reportType === 'Page Views'
								? 'contained'
								: 'outlined'
						}
					>
						Page Views
					</Button>
					<Button
						onClick={() => setReportType('Event Counts')}
						variant={
							reportType === 'Event Counts'
								? 'contained'
								: 'outlined'
						}
					>
						Event Counts
					</Button>
				</ButtonGroup>
			</Box>

			{/* Add your components and logic here */}
			{reportType === 'Basic Stats' && (
				<BasicStatsData basicStatsData={basicStats} />
			)}
			{reportType === 'Visit Stats' && (
				<VisitStatsChart apiResponse={visitStats} />
			)}
			{reportType === 'User Sources' && (
				<UserSourceChart apiResponse={userSources} />
			)}
			{reportType === 'Page Views' && (
				<PageViewsChart apiResponse={pageViews} />
			)}
			{reportType === 'Event Counts' && (
				<EventCountChart apiResponse={eventCounts} />
			)}
		</Box>
	);
};

export default AnalyticsReport;
